import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  ChartContainer,
  ChartTooltip
} from "@/components/ui/chart";
import {
  PieChart,
  Pie,
  LineChart,
  Line,
  BarChart,
  Bar,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// Constant colors for the charts
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

interface DoctorRevenueData {
  name: string;
  value: number;
}

interface DateRevenueData {
  name: string;
  revenue: number;
}

interface DoctorData {
  doctorId: string;
  doctorName: string;
  revenue: number;
}

interface PatientsByDoctorData {
  doctorId: string;
  doctorName: string;
  count: number;
}

interface PaymentMethodData {
  method: string;
  revenue: number;
}

interface MonthlyTrendData {
  month: string;
  revenue: number;
  patients: number;
}

interface RevenueForecastData {
  date: string;
  forecast: number;
}

interface RevenueChartsProps {
  doctorRevenueData: DoctorRevenueData[];
  dateRevenueData: DateRevenueData[];
  doctorRevenue: DoctorData[];
  patientsByDoctor: PatientsByDoctorData[];
  paymentMethodRevenue: PaymentMethodData[];
}

// Add this helper function at the top of the file
const formatCurrency = (value: number | string) => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  return `₹${numValue.toLocaleString('en-IN')}`;
};

export function RevenueCharts({ 
  doctorRevenueData, 
  dateRevenueData, 
  doctorRevenue, 
  patientsByDoctor,
  paymentMethodRevenue
}: RevenueChartsProps) {
  // Check if there's data to display
  const hasData = doctorRevenueData.length > 0 || dateRevenueData.length > 0;
  
  if (!hasData) {
    return (
      <div className="text-center py-12">
        <div className="text-4xl mb-4">📈</div>
        <h3 className="text-lg font-semibold mb-2">No Revenue Data Available</h3>
        <p className="text-muted-foreground">
          Revenue charts will appear here once you have completed patient consultations and payments.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Revenue by Doctor</CardTitle>
            <CardDescription>Revenue distribution across all doctors</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={doctorRevenueData}
                    innerRadius={50}
                    outerRadius={80}
                    dataKey="value"
                    nameKey="name"
                    label={(entry) => entry.name}
                    labelLine={false}
                  >
                    {doctorRevenueData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => [formatCurrency(value), 'Revenue']} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Payment Methods</CardTitle>
            <CardDescription>Revenue by payment method</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={paymentMethodRevenue}
                    dataKey="revenue"
                    nameKey="method"
                    cx="50%"
                    cy="50%"
                    outerRadius={60}
                    label={(entry) => `${entry.method}`}
                    labelLine={false}
                  >
                    {paymentMethodRevenue.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => [formatCurrency(value), 'Revenue']} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
